<script setup lang='ts'>
import { onMounted, useTemplateRef, computed, onUnmounted, ref, watch } from 'vue';
import useCheckout from '@/apps/checkout/composables/useCheckout';
import { useStripePayment } from '@/apps/checkout/composables/useStripePayment';

const {wooCheckout, step, wooCart} = useCheckout();
const {initStripe, paymentConfirmationError, setSourceResponse, iframeSource} = useStripePayment();

const iframeElement = useTemplateRef('stripe-iframe');

const isPaymentMethodEnabled = computed(() => {
	return wooCart.cart.value?.payment_methods.includes("stripe");
});

const cardError = ref();

function messageListener(messageEv: MessageEvent){
	if(messageEv.data.type == 'stripe-element-event' && messageEv.data.subType == 'change'){
		if(messageEv.data.elementType == 'card') cardError.value = messageEv.data?.payload?.error?.message;
	}else if(messageEv.data.type == 'stripe-element-event' && messageEv.data.subType == 'ready'){
		//console.log('messageListener', messageEv.data);
	}else if(messageEv.data.type == 'stripe-source' && messageEv.data.subType == 'on-pay'){
		setSourceResponse(messageEv.data?.payload);
	}else if('stripe-source-error' == messageEv.data.type){
		console.warn('stripe-source-error', messageEv.data);
		if(messageEv.data?.payload?.message) cardError.value = messageEv.data.payload.message;
		setSourceResponse(null);
	}
}

onMounted(() => {
	window.removeEventListener('message', messageListener);
	if(isPaymentMethodEnabled.value){
		window.addEventListener('message', messageListener);
		initStripe();
	}
});

onUnmounted(() => {
	window.removeEventListener('message', messageListener);
});

watch(iframeElement, () => {
	iframeSource.value = iframeElement.value;
});

const paymentDescriptor = computed(() => {
	return wooCart.cart.value?.extensions.itlp?.payment_method_descriptions['stripe'];
});

const isPaymentMethodSelected = computed(() => {
	return wooCheckout.selectedPaymentMethodId.value == 'stripe';
});

const errorMessage = computed(() => {
	if(!isPaymentMethodSelected.value) return;
	if(paymentConfirmationError.value?.message){
		return paymentConfirmationError.value.message;
	}
	if(wooCheckout.postOrderError.value instanceof Response && wooCheckout.postOrderBodyError.value){
		if(!wooCheckout.postOrderBodyError.value.payment_result || wooCheckout.postOrderBodyError.value.payment_result.payment_status != 'failure') return
		const message = wooCheckout.postOrderBodyError.value?.payment_result.payment_details.find((d:any) => d.key == 'errorMessage');
		return message && message.value;
	}
	if(cardError.value){
		if(typeof(cardError.value) == 'string') return cardError.value;
	}
});

const iframeSrc = computed(() => {
	if(!isPaymentMethodEnabled.value || !wooCart.cart.value?.extensions?.itlp?.stripe_pkey) return null;
	return (window.wp_base_url || '')+'/wp-content/mu-plugins/italpreziosi/frontend/assets/itlp-app/stripe-payment.html?'+(new URLSearchParams({
		parentOrigin: window.location.origin,
		lang: window?.itlp_websocket_config?.lang || '',
		amount: wooCart.cart.value!.totals.total_price,
		publicKey: wooCart.cart.value.extensions.itlp.stripe_pkey
	}).toString())
})

</script>

<template lang='pug'>
button.itlp-checkout-payment-row(v-if='isPaymentMethodEnabled' 
	:class="{'itlp-checkout-payment-selected': isPaymentMethodSelected}"
	@click='wooCheckout.selectedPaymentMethodId.value = "stripe"'
	:aria-label="paymentDescriptor?.title"
	:aria-pressed="isPaymentMethodSelected? 'true' : 'false'"
)
	.itlp-checkout-payment.itlp-checkout-payment-stripe(
		:class="{'itlp-checkout-payment-error': Boolean(errorMessage)}" 
	)
		.itlp-checkout-payment-fake-radio
		.itlp-checkout-payment-title {{paymentDescriptor?.title}}
		.itlp-checkout-payment-description {{paymentDescriptor?.description}}
		.itlp-checkout-payment-stripe-card
			iframe#stripeCustomIframe(ref='stripe-iframe' v-if="iframeSrc" frameborder="0" height="40" allow="payment *" :src="iframeSrc")
		.itlp-checkout-payment-stripe-error(v-if='errorMessage') {{errorMessage}} 

</template>